import React, { useEffect, useState } from "react";
import {
  Tabs,
  Tab,
  Box,
  Typography,
  CircularProgress,
  Button,
  styled,
  useTheme,
  useMediaQuery,
  Pagination,
} from "@mui/material";
import tabs from "./config";
import { getApiData } from "../../utils/APIHelper";
import { useNavigate } from "react-router-dom";
import ProposalDetails from "./components/ProposalDetails";
import MobileProposalCards from "./components/MobileProposalCards";
import authActions from "../../redux/reducers/auth/actions";
import { useDispatch } from "react-redux";
import { updateUserData } from "../../utils/CommonFunction";
import { useTokenValidation } from "../../components/hooks/useTokenValidation";
import SecurityPolicyModal from "../../components/SecurityPolicyModal";
import Permission from "../../components/Permission";

const StyledHeading = styled("h2")({
  color: "#0D0D0E",
  fontFamily: "Inter Tight",
  fontSize: "24px",
  fontWeight: "500",
});

const StyledButton = styled(Button)(({ theme }) => ({
  background: "#4D6BF6",
  color: "#ffffff",
  boxShadow: "none",
  border: "none",
  whiteSpace: "nowrap",
  fontSize: "16px",
  fontWeight: "800",
  borderRadius: "10px",
  padding: ".5vh .7vw",
  letterSpacing: "0",
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  display: "flex",
  gap: "10px",
  padding: "0 2vw",
}));

const StyledTab = styled(Tab)(({ theme, selected }) => ({
  color: selected ? "#fff !important" : "#949DAA",
  backgroundColor: selected ? "#0d0d0d" : "#fff",
  border: "1px solid #d7d8d9",
  borderRadius: "10px",
  minHeight: "40px !important",
  fontWeight: 500,
  textTransform: "none",
  marginRight: "10px",
  padding: "4px 16px !important",
  fontFamily: "Inter Tight",
  "&:hover": {
    backgroundColor: selected ? "#0d0d0d" : "",
    color: !selected && "#000",
  },
  "& .MuiTabs-indicator": {
    backgroundColor: "transparent !important",
  },
}));

// Mobile-specific styled components
const MobileHeading = styled(Typography)(({ theme }) => ({
  fontSize: "22px",
  fontWeight: "600",
  color: "#0D0D0E",
  fontFamily: "Inter Tight",
  letterSpacing: "0",
}));

const MobileCreateButton = styled(Button)(({ theme }) => ({
  backgroundColor: "#4D6BF6",
  color: "#ffffff",
  borderRadius: "8px",
  fontSize: "14px",
  fontWeight: "500",
  textTransform: "none",
  padding: "6px 16px",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "#4D6BF6",
    boxShadow: "none",
  },
}));

const MobileHeaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "2vh 2vw 2vh 2vw",
}));

const MobileStyledTabs = styled(Tabs)(({ theme }) => ({
  display: "flex",
  gap: "10px",
  padding: "0 2vw",
  margin: "1vh 0!important",
  overflowX: "auto",
  overflowY: "hidden",
  scrollbarWidth: "none", // Firefox
  msOverflowStyle: "none", // IE and Edge
  "&::-webkit-scrollbar": {
    display: "none", // Chrome, Safari, Opera
  },
  "& .MuiTabs-scroller": {
    overflow: "visible",
  },
  "& .MuiTabs-flexContainer": {
    gap: "8px",
    paddingBottom: "4px",
    overflowX: "auto",
    overflowY: "hidden",
    scrollbarWidth: "none", // Firefox
    msOverflowStyle: "none", // IE and Edge
    "&::-webkit-scrollbar": {
      display: "none", // Chrome, Safari, Opera
    },
  },
}));

const MobileStyledTab = styled(Tab)(({ theme, selected }) => ({
  color: selected ? "#fff !important" : "#949DAA",
  backgroundColor: selected ? "#0d0d0d" : "#fff",
  border: "1px solid #d7d8d9",
  borderRadius: "8px",
  minHeight: "36px !important",
  maxHeight: "36px",
  maxWidth: "140px !important",
  minWidth: "50px !important",
  fontWeight: 500,
  textTransform: "none",
  marginRight: "6px",
  padding: "4px 12px !important",
  fontFamily: "Inter Tight",
  fontSize: "13px",
  lineHeight: "1.2",
  whiteSpace: "nowrap",
  flexShrink: 0,
  "&:hover": {
    backgroundColor: selected ? "#0d0d0d" : "",
    color: !selected && "#000",
  },
  "& .MuiTabs-indicator": {
    backgroundColor: "transparent !important",
  },
}));

export default function Proposals() {
  const { resetProposalDetails } = authActions;
  const [selectedTab, setSelectedTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState([]);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);

  // Token validation hook
  const { showSecurityModal, handleSecurityModalClose } = useTokenValidation();

  useEffect(() => {
    updateUserData();
  }, []);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    setDetails([]);
  };

  const fetchData = async (currentPage) => {
    setLoading(true);
    const selectedType = tabs[selectedTab]?.type;
    let baseUrl = "";

    if (selectedTab === 0) {
      baseUrl = `api/project/list-contractor-project`;
    } else if (selectedTab === 1) {
      baseUrl = "api/project/proposal-drafts-list";
    } else {
      baseUrl = `api/project/list-contractor-project?status=${selectedType}`;
    }

    let url;
    if (selectedTab === 1) {
      url = baseUrl;
    } else {
      url =
        currentPage === 1
          ? `${baseUrl}${baseUrl.includes("?") ? "&" : "?"}per_page=10`
          : `${baseUrl}${
              baseUrl.includes("?") ? "&" : "?"
            }page=${currentPage}&per_page=10`;
    }

    try {
      const response = await getApiData(url, "GET", {}, {}, false);
      const newDetails = response?.data || [];
      setTotalPages(response?.total_pages || 0);
      setDetails(newDetails);
    } catch (error) {
      console.error("Something went wrong", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(page);
  }, [selectedTab, page]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleCreateProposal = () => {
    navigate("/new-create-proposal");
  };

  return (
    <div style={{ height: "100%" }}>
      {/* Security Policy Modal */}
      <SecurityPolicyModal
        open={showSecurityModal}
        onConfirm={handleSecurityModalClose}
      />

      <Box
        sx={{
          typography: "body1",
          backgroundColor: "#fff",
          height: "100%",
          borderRadius: "6px",
          fontFamily: "Inter Tight",
        }}
      >
        {isMobile ? (
          <div style={{ padding: "1rem" }}>
            {/* Mobile Header */}
            <MobileHeaderContainer>
              <MobileHeading>Proposals</MobileHeading>
              {/* <MobileCreateButton
                variant="contained"
                onClick={handleCreateProposal}
              >
                Add Proposal
              </MobileCreateButton> */}
            </MobileHeaderContainer>

            <MobileStyledTabs
              value={selectedTab}
              onChange={handleTabChange}
              aria-label="Styled Tabs"
              sx={{
                "& .MuiTabs-indicator": {
                  background: "none",
                },
              }}
            >
              {tabs?.map((tab, index) => (
                <MobileStyledTab
                  key={tab.id}
                  label={tab.title}
                  selected={selectedTab === index}
                />
              ))}
            </MobileStyledTabs>

            <MobileProposalCards
              details={details}
              selectedTab={selectedTab}
              loading={loading}
            />
            {selectedTab !== 1 && (
              <center
                style={{
                  alignSelf: "center",
                  width: "100%",
                  justifyContent: "center",
                  alignItems: "center",
                  display: "flex",
                }}
              >
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  style={{
                    alignSelf: "center",
                    marginTop: "1vh",
                  }}
                />
              </center>
            )}
          </div>
        ) : (
          <>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "4vh 2vw 2vh 2vw",
                background:
                  "linear-gradient(180deg, rgba(77, 107, 246, 0.30) 0%, rgba(255, 255, 255, 0.00) 100%)",
              }}
            >
              <StyledHeading>Proposals</StyledHeading>
              <Permission resource="PROJECTS" action="CREATE">
                <StyledButton variant="contained" onClick={handleCreateProposal}>
                  <p style={{ fontWeight: "500" }}>Create Proposal</p>
                </StyledButton>
              </Permission>
            </div>
            <StyledTabs
              value={selectedTab}
              onChange={handleTabChange}
              aria-label="Styled Tabs"
              sx={{
                "& .MuiTabs-indicator": {
                  background: "none",
                },
              }}
            >
              {tabs?.map((tab, index) => (
                <StyledTab
                  key={tab.id}
                  label={tab.title}
                  selected={selectedTab === index}
                />
              ))}
            </StyledTabs>
            <Box sx={{ padding: "0 2vw" }}>
              {loading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: 200,
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : details?.length > 0 ? (
                <>
                  <ProposalDetails
                    details={details}
                    selectedTab={selectedTab}
                  />
                  {selectedTab !== 1 && (
                    <center
                      style={{
                        alignSelf: "center",
                        width: "100%",
                        justifyContent: "center",
                        alignItems: "center",
                        display: "flex",
                      }}
                    >
                      <Pagination
                        count={totalPages}
                        page={page}
                        onChange={handlePageChange}
                        color="primary"
                        style={{
                          alignSelf: "center",
                          marginTop: "1vh",
                        }}
                      />
                    </center>
                  )}
                </>
              ) : (
                <Typography
                  variant="body1"
                  sx={{ textAlign: "center", marginTop: 4 }}
                >
                  No data available
                </Typography>
              )}
            </Box>
          </>
        )}
      </Box>
    </div>
  );
}
